import { ConfigPeriod } from "@types"
import { useI18n } from "vue-i18n"

/**
 * Composable pour les fonctions d'aides à la création
 * des phrases
 */
export const useLangHelpers = () => {
    const { t } = useI18n({
        useScope: "global",
        messages: {
            fr: {
                freq_unvailable:
                    "la fréquence {freq} n'est pas disponible. | les fréquences {freq} ne sont pas disponibles.",
                freq_available:
                    "la fréquence {freq} est limitée à des {sentence}.",
                period_available:
                    "la période d'analyse est limitée à {sentence}.",
                period_invalid:
                    "La période sélectionnée est trop longue pour cette variable, veuillez sélectionner une période inférieure à : {sentence}",
            },
        },
    })

    /**
     * Permet de construire la phrase indiquant les fréquences non disponibles
     * @param freqs Liste des fréquences non disponibles
     * @example
     * - "la fréquence journalière n'est pas disponible"
     * - "les fréquences journalière, hebdomadaire ne sont pas disponibles"
     *
     * @returns La phrase indiquant les fréquences non disponibles
     */
    function buildFreqDisabledSentence(freqs: string[]) {
        const freqsFormated = freqs
            .map((freq) => t(`freq.${freq}.long`).toLowerCase())
            .join(", ")
        return t(
            "freq_unvailable",
            {
                freq: freqsFormated,
            },
            freqs.length
        )
    }

    /**
     * Permet de construire la phrase indiquant les fréquences disponibles
     * avec les limitations sur la période
     * @param configPeriods Configuration des périodes
     *
     * @example
     * - "la fréquence journalière est limitée à des périodes de 1 mois maximum"
     * - "la fréquence mensuelle est limitée à des périodes de 1 mois minimum"
     *
     * @returns La phrase indiquant la première fréquence disponible avec ses limites. Si la première fréquence n'a pas de limite (max ou min), retourne null
     */
    function buildFirstFreqAvailableSentence(configPeriods: ConfigPeriod) {
        // Trouve la première fréquence non désactivée
        const firstFreq = Object.entries(configPeriods ?? {})
            .filter(([, config]) => !config.disabled)
            ?.map(([freq, config]) => {
                return {
                    freq: freq,
                    ...config,
                }
            })?.[0]
        if (!firstFreq) return

        const type = firstFreq?.max ? "max" : firstFreq?.min ? "min" : ""

        if (!type) return

        if (!firstFreq[type]?.quantity) return

        if (!firstFreq[type]?.unit) return

        const freqName = t(`freq.${firstFreq?.freq}.long`).toLowerCase()

        const sentenceLimitPeriod = convertLimiPeriodToSentence({
            type,
            quantity: firstFreq[type].quantity,
            unit: firstFreq[type].unit,
        })
        return t("freq_available", {
            freq: freqName,
            sentence: sentenceLimitPeriod,
        })
    }

    /**
     * Permet de convertir une limite de période en une phrase de type "Période(s) {de|d'} X {unit} {maximum|minimum}"
     * @param type - Type de limite (max ou min)
     * @param quantity - Quantité
     * @param unit - Unité de la période (day, month, week)
     * @param version - Version de la phrase
     *
     * @example
     * Avec version 1 :
     * - "périodes de 1 mois maximum"
     * - "périodes d'un mois minimum"
     * - "périodes d'une semaine maximum"
     *
     * Avec version 2 :
     * - "un 1 mois maximum"
     * - "un 1 mois minimum"
     * - "une semaine maximum"
     * - "3 semaines maximum"
     */
    function convertLimiPeriodToSentence(
        {
            type,
            quantity,
            unit,
            version = 1,
        }: {
            type: "max" | "min"
            quantity: number
            unit: "day" | "month" | "week" | "year"
            version?: number
        } = {
            type: "max",
            quantity: 1,
            unit: "day",
            version: 1,
        }
    ) {
        const unitOfTime = t(`unit_of_time.${unit}`, { count: quantity })
        const typeSentence = t(type)
        let quantitySentence

        switch (version) {
            case 1:
                quantitySentence =
                    quantity > 1
                        ? `de ${quantity}`
                        : ["week"].includes(unit)
                          ? "d'une"
                          : "d'un"
                return `${t("period", { count: 2 })} ${quantitySentence} ${unitOfTime} ${typeSentence}`
            case 2:
                quantitySentence =
                    quantity > 1
                        ? `${quantity}`
                        : ["week"].includes(unit)
                          ? "une"
                          : "un"
                return `${quantitySentence} ${unitOfTime} ${typeSentence}`
        }

        return
    }

    /**
     * Permet de construire une phrase d'information concernant les fréquences
     * @param variable - Variable analysée
     * @param freq - Fréquence sélectionnée
     * @param minFreq - Fréquence minimale possible
     * @returns La phrase d'information
     *
     * @example
     * buildInfoFreq("energie_elec_tout", "hourly", "daily")
     * // Réponse: "La consommation de gaz ne peut être analysée qu'à partir de la fréquence journalière. Les fréquences plus fines, comme la fréquence horaire, n'ont pas de sens pour ce type d'analyse."
     */
    function buildInfoFreq(variable: string, freq: string, minFreq?: string) {
        const freqFormated = t(`freq.${freq}.long`).toLowerCase()

        const minFreqFormated = t(`freq.${minFreq}.long`).toLowerCase()

        const variableFormated = t(`variables.${variable}.sentence`)

        const analyseVerb = variableFormated.toLowerCase().includes("la")
            ? "analysée"
            : "analysé"

        const sentence = `${variableFormated} ne peut être ${analyseVerb} qu'à partir de la fréquence ${minFreqFormated}. Les fréquences plus fines, comme ${freqFormated}, n'ont pas de sens pour ce type d'analyse.`
        return sentence
    }

    /**
     * Permet de construire une phrase d'avertissement concernant les fréquences non disponibles et disponibles
     * @param configPeriods - Configuration des périodes
     * @param total - Nombre total de sources
     * @returns La phrase d'avertissement
     *
     * @example
     * buildWarningVariable({hourly: { disabled: true }, daily: {disabled: false, max: { quantity: 1, unit: "month"}}}, 10)
     * // Réponse: "Attention, en raison du nombre de sources sélectionnées (10 sources), la fréquence horaire n'est pas disponible. Vous avez choisi la fréquence journalière: la période d'analyse est limitée à des périodes de 1 mois maximum."
     */
    function buildWarningVariable(configPeriods: ConfigPeriod, total: number) {
        const freqs = Object.entries(configPeriods ?? {})
            ?.filter(([, value]) => value?.disabled)
            ?.map(([key]) => key)

        const unvailable_freq = buildFreqDisabledSentence(freqs)
        const available_freq = buildFirstFreqAvailableSentence(
            configPeriods ?? {}
        )

        let sentence = `Attention, en raison du nombre de sources sélectionnées (${total} sources)`

        if (freqs.length > 1) {
            sentence += `, ${unvailable_freq} et`
        }

        sentence += ` ${available_freq}`

        return sentence
    }

    /**
     * Permet de construire la phrase d'avertissement concernant la fréquence
     * @param configPeriods - Configuration des périodes
     * @param freq - Fréquence sélectionnée
     * @param total - Total de sources
     * @returns La phrase d'avertissement
     *
     * @example
     * // (cas où la fréquence n'est pas disponible)
     * buildWarningFreq({hourly: { disabled: true }, daily: {disabled: false, max: { quantity: 1, unit: "month"}}}, "hourly", 10)
     * // Réponse: "Attention, en raison du nombre de sources sélectionnées (10 sources), la fréquence journalière n'est pas disponible."
     *
     * // (cas où la fréquence est disponible)
     * buildWarningFreq({hourly: { disabled: true }, daily: {disabled: false, max: { quantity: 1, unit: "month"}}}, "daily", 10)
     * // Réponse: "Vous avez choisi la fréquence journalière: la période d'analyse est limitée à des périodes de 1 mois maximum."
     */
    function buildWarningFreq(
        configPeriods: ConfigPeriod,
        freq: string,
        total: number
    ) {
        const configFreq = configPeriods[freq]

        if (!configFreq?.max) {
            let sentence = `Attention, en raison du nombre de sources sélectionnées (${total} sources)`
            sentence += `, la fréquence ${t(`freq.${freq}.long`).toLowerCase()} n'est pas disponible.`
            return sentence
        }

        const sentenceLimitMaxPeriod = convertLimiPeriodToSentence({
            type: "max",
            quantity: configFreq.max.quantity,
            unit: configFreq.max.unit,
            version: 2,
        })

        const freqName = t(`freq.${freq}.long`).toLowerCase()

        return `Vous avez choisi la fréquence ${freqName}: ${t(
            "period_available",
            {
                freq: freqName,
                sentence: sentenceLimitMaxPeriod,
            },
            0
        )}`
    }

    function buildErrorPeriod(
        configPeriods: ConfigPeriod,
        freq: string,
        total: number
    ) {
        const configFreq = configPeriods[freq]

        if (!configFreq?.max) {
            let sentence = `Attention, en raison du nombre de sources sélectionnées (${total} sources)`
            sentence += `, la fréquence ${t(`freq.${freq}.long`).toLowerCase()} n'est pas disponible.`
            return sentence
        }

        const sentenceLimitMaxPeriod = convertLimiPeriodToSentence({
            type: "max",
            quantity: configFreq.max.quantity,
            unit: configFreq.max.unit,
            version: 2,
        })

        const freqName = t(`freq.${freq}.long`).toLowerCase()

        return t(
            "period_invalid",
            {
                freq: freqName,
                sentence: sentenceLimitMaxPeriod,
            },
            0
        )
    }

    return {
        buildWarningVariable,
        buildWarningFreq,
        buildInfoFreq,
        buildErrorPeriod,
    }
}
